// Status vocabularies and criteria prose for the data-overlay UI (TGCLOUD-1670).
//
// No note status field has a schema enum. The forms write a fixed list, the
// Desktop import and an unvalidated PUT accept any string, pathway-created
// appointments carry no status at all, and the demo seeds write a value no form
// can produce. So this file is a BEST-KNOWN list, not a closed set: the picker
// offers these plus whatever the open family actually contains, marked
// "observed", and the evaluator compares case-insensitively either way.
//
// The vocabulary is no longer copied from the forms: the forms render whatever a
// site has configured, and what this product ships is declared once in
// `backend/config/lookupDefinitions.js` and mirrored in `src/util/statusSemantics`,
// which `statusSemantics.test.js` compares against the server structurally.

import { OP_LABELS } from './operators';
import { RESOLVED_APPOINTMENT_STATUSES } from 'src/util/recordDueDates';
import { LIST_BY_FIELD, fallbackLabelsFor } from 'src/util/statusSemantics';

/**
 * The wording this product ships, per field.
 *
 * DERIVED rather than copied (TGCLOUD-1784). These used to be four hand-kept
 * arrays with the form's line number beside each, and a test that read the form
 * source to stop them drifting. The forms no longer hold a literal list at all
 * -- they render whatever a site has configured -- so the copy has moved to one
 * place, and the picker's real answer now comes from the fetched rows via
 * `statusOptions` below, with this as the floor.
 */
export const STATUS_VOCABULARY = Object.freeze(
  Object.fromEntries(
    Object.entries(LIST_BY_FIELD).map(([field, listKey]) => [
      field,
      Object.freeze(fallbackLabelsFor(listKey))
    ])
  )
);

/**
 * Everything the picker should offer for a field: the form's list first, then
 * any value the open family actually holds that the forms cannot produce
 * (a Desktop import, an old API write, a demo seed), flagged so the clinician
 * can see it is not a value they could pick when creating a record.
 */
export function statusOptions(field, facts, noteType) {
  const known = STATUS_VOCABULARY[field] || [];
  const seen = new Map(known.map((v) => [v.toLowerCase(), { value: v }]));
  for (const person of Object.values(facts?.members || {})) {
    for (const row of person?.notes?.[noteType] || []) {
      const raw = row?.[field];
      if (raw == null || raw === '') continue;
      const key = String(raw).trim().toLowerCase();
      if (seen.has(key)) continue;
      seen.set(key, { value: String(raw), observed: true });
    }
  }
  return [...seen.values()];
}

// Field names as a clinician reads them. Anything absent falls back to the
// stored name split on camel case, which is right often enough that maintaining
// a total map is not worth the drift.
const FIELD_LABELS = Object.freeze({
  appointmentStatus: 'status',
  appointmentDate: 'appointment date',
  appointmentType: 'appointment type',
  contactStatus: 'status',
  contactDate: 'contact date',
  contactType: 'contact type',
  contactMode: 'contact mode',
  referralStatus: 'status',
  referralDate: 'referral date',
  referralTriage: 'triage',
  consentRequestDate: 'request date',
  consentDate: 'consent date',
  response: 'response',
  dateTaken: 'date taken',
  sampleStatus: 'sample status',
  dateRequested: 'date requested',
  dateResultDue: 'result due date',
  dateResultReceived: 'result received date',
  testResult: 'result',
  reminderEnabled: 'reminder',
  reminderDate: 'reminder date',
  requestCheckingCount: 'checking requests',
  approved: 'approved',
  clinicalNoteDate: 'note date',
  caution: 'caution flag',
  pinned: 'pinned',
  displayInPedigree: 'marked for pedigree',
  processStatus: 'process status',
  processStartDate: 'start date',
  endDate: 'end date',
  treatmentDate: 'treatment date',
  treatmentEndDate: 'treatment end date',
  recordsReviewed: 'records reviewed',
  submitted: 'questionnaire submitted',
  'personTags.tag': 'person tag',
  $deceased: 'deceased',
  $age: 'age',
  $carrierResult: 'carrier result',
  '$address.hasPrimary': 'primary address',
  '$address.hasCorrespondence': 'correspondence address',
  '$address.count': 'address records'
});

const humanise = (field) =>
  String(field)
    .replace(/^\$/, '')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .toLowerCase();

const DERIVED_RE = /^\$(daysSince|daysUntil)\((.+)\)$/;

export const fieldLabel = (field) => {
  const derived = DERIVED_RE.exec(String(field || ''));
  if (derived) {
    const inner = fieldLabel(derived[2]);
    return derived[1] === 'daysSince'
      ? `days since ${inner}`
      : `days until ${inner}`;
  }
  return FIELD_LABELS[field] || humanise(field);
};

const listPhrase = (values) => {
  if (!values.length) return 'nothing chosen yet';
  const parts = values.map((v) => (v == null ? 'nothing recorded' : String(v)));
  if (parts.length === 1) return parts[0];
  return `${parts.slice(0, -1).join(', ')} or ${parts[parts.length - 1]}`;
};

/** Render one materialised value as the clinician would say it. */
export function valuePhrase(value) {
  if (value == null) return 'nothing recorded';
  if (value === true) return 'yes';
  if (value === false) return 'no';
  if (Array.isArray(value)) return listPhrase(value);
  if (typeof value === 'object') {
    if (value.rel === 'today') {
      const n = Number(value.offsetDays) || 0;
      if (!n) return 'today';
      return n > 0 ? `${n} days from today` : `${Math.abs(n)} days ago`;
    }
    return JSON.stringify(value);
  }
  return String(value);
}

// A member array's element, named the way a clinician names one of them.
const ARRAY_NOUNS = Object.freeze({
  phenotypes: 'phenotype',
  diagnoses: 'diagnosis',
  genotypes: 'genotype',
  'genotypes.variants': 'variant'
});

// `id` means a different thing in each member array, and "id is not chosen yet"
// tells the reader nothing about what they have to pick.
const ARRAY_ID_LABELS = Object.freeze({
  phenotypes: 'phenotype',
  diagnoses: 'condition',
  genotypes: 'gene',
  'genotypes.variants': 'variant'
});

// OP_LABELS is date-flavoured on purpose ("is after"), because that is how the
// Data Explorer's filter panel words a comparison and most comparisons in this
// vocabulary are on dates. A reduced COUNT or day-difference is a number, and
// "the total is after 0" is not English, so the reduced-value clause gets its
// own numeric wording.
const NUMERIC_OP_LABELS = Object.freeze({
  equals: 'is',
  notEquals: 'is not',
  greaterThan: 'is more than',
  lessThan: 'is less than',
  greaterThanOrEqual: 'is at least',
  lessThanOrEqual: 'is at most'
});

const predicatePhrase = ({ field, op, value }, source) => {
  const label =
    field === 'id' && source?.type === 'memberArray'
      ? ARRAY_ID_LABELS[source.path] || fieldLabel(field)
      : fieldLabel(field);
  if (op === 'exists') return `${label} is recorded`;
  if (op === 'notExists') return `${label} is not recorded`;
  if (value === true) return `${label} ${op === 'equals' ? 'is' : 'is not'} on`;
  if (value === false)
    return `${label} ${op === 'equals' ? 'is' : 'is not'} off`;
  // A parameter the clinician has not filled in yet. Saying "is nothing
  // recorded" would read as a claim about the data rather than about the form.
  if (value == null || (Array.isArray(value) && !value.length)) {
    return `${label} is not chosen yet`;
  }
  // A plain number is a count, an age or a day difference, never a date, so the
  // date-flavoured comparison labels would misread it.
  const numeric = typeof value === 'number';
  // A set of one reads as a single value. Several presets now compare against a
  // vocabulary rather than one literal (TGCLOUD-1784), because a site can have
  // more than one wording meaning the same thing; where it has only one, "is
  // none of Closed" is not how anyone says it.
  const single = Array.isArray(value) && value.length === 1;
  const effectiveOp = single
    ? { in: 'equals', notIn: 'notEquals' }[op] || op
    : op;
  const opLabel =
    (numeric ? NUMERIC_OP_LABELS[effectiveOp] : null) ||
    OP_LABELS[effectiveOp] ||
    effectiveOp;
  return `${label} ${opLabel} ${valuePhrase(single ? value[0] : value)}`;
};

const REDUCER_PHRASE = Object.freeze({
  count: 'counted',
  latest: 'taking the most recent',
  earliest: 'taking the earliest',
  min: 'taking the smallest',
  max: 'taking the largest'
});

const lowerFirst = (text) =>
  text ? text.charAt(0).toLowerCase() + text.slice(1) : text;
const upperFirst = (text) =>
  text ? text.charAt(0).toUpperCase() + text.slice(1) : text;

const sourceNoun = (source) => {
  if (source?.type === 'note') return `${source.noteType} record`;
  if (source?.type === 'memberArray') {
    return ARRAY_NOUNS[source.path] || String(source.path);
  }
  return 'family member record';
};

/**
 * The materialised criteria as one readable sentence, shown at the top of the
 * criteria popover. It is generated from the same spec the evaluator runs, so a
 * clinician reading it is reading what will actually be matched, not a
 * description someone typed alongside it and forgot to update.
 */
export function criteriaSentence(spec) {
  if (!spec) return '';
  const noun = sourceNoun(spec.source);
  const where = (spec.where || []).map((p) => predicatePhrase(p, spec.source));

  let sentence;
  if (spec.reduce?.kind === 'none') {
    sentence = where.length
      ? `No ${noun} where ${where.join(', ')}`
      : `No ${noun} at all`;
  } else if (where.length) {
    sentence = `${noun} where ${where.join(', ')}`;
  } else {
    sentence = `Any ${noun}`;
  }

  const kind = spec.reduce?.kind;
  if (kind === 'count') {
    sentence += ', counted';
  } else if (REDUCER_PHRASE[kind]) {
    sentence += `, ${REDUCER_PHRASE[kind]} ${fieldLabel(spec.reduce.field)}`;
  }
  if (spec.then) {
    const subject = kind === 'count' ? 'the total' : 'that';
    const op =
      NUMERIC_OP_LABELS[spec.then.op] ||
      OP_LABELS[spec.then.op] ||
      spec.then.op;
    sentence += `, where ${subject} ${op} ${valuePhrase(spec.then.value)}`;
  }
  // Only the FIRST character is lowered when a sub-clause is folded in.
  // Lowercasing the whole clause corrupts the values inside it, turning
  // "status is not Closed" into "status is not closed" and
  // "Did Not Attend" into something no record holds.
  for (const sub of spec.and || []) {
    sentence += `, and ${lowerFirst(criteriaSentence(sub))}`;
  }
  for (const sub of spec.andNot || []) {
    sentence += `, and not ${lowerFirst(criteriaSentence(sub))}`;
  }
  return upperFirst(sentence);
}

/** Options a family-scoped param draws from the open pedigree. */
export function familyOptions(source, { memberRecords = [], diagnoses = [] }) {
  const collect = (fn) => {
    const seen = new Map();
    for (const m of memberRecords) {
      for (const opt of fn(m) || []) {
        if (!opt || opt.value == null || opt.value === '') continue;
        if (!seen.has(String(opt.value))) seen.set(String(opt.value), opt);
      }
    }
    return [...seen.values()].sort((a, b) =>
      String(a.label).localeCompare(String(b.label))
    );
  };

  switch (source) {
    case 'familyPersonTags':
      return collect((m) =>
        (m.personTags || []).map((t) => ({
          value: t?.tag ?? t,
          label: String(t?.tag ?? t)
        }))
      );
    case 'familyPhenotypes':
      return collect((m) =>
        (m.phenotypes || []).map((p) => ({
          value: p?.id,
          label: p?.name || p?.label || String(p?.id)
        }))
      );
    case 'familyGenes':
      return collect((m) =>
        (m.genotypes || []).map((g) => ({
          value: g?.gene,
          label: String(g?.gene)
        }))
      );
    case 'familyDiagnoses':
      return (diagnoses || [])
        .filter((d) => d && (d._id || d.id))
        .map((d) => ({ value: d._id || d.id, label: d.name || d.title || '' }))
        .sort((a, b) => String(a.label).localeCompare(String(b.label)));
    default:
      return [];
  }
}

export const RESOLVED_APPOINTMENT_LABEL = listPhrase([
  ...RESOLVED_APPOINTMENT_STATUSES
]);
